import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MinimapCropPan, ReactIcons } from "@kalyzee/kalyzee-ui";
import { ContentLocationOutputDTO, VOD_STATE, VodOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { FullscreenContainer, FullscreenContainerRef, Loading, MultipleVideoPlayer, MultipleVideoPlayerDateDisplayMode, MultipleVideoPlayerDebugState, MultipleVideoPlayerRef, MultipleVideoPlayerRenderingContext, PlayerRenderItem, VideoSourceDated } from "@kalyzee/kast-web-sdk/web-components";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { useSettings } from "../../../../hooks/settings";
import { useGridConfig } from "../../../../hooks/gridConfig";
import { MultiVideoPlayerDebugPane } from "../../../../components/debug/MultiVideoPlayerDebugPane";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { focusIconForLayout, NewTriLayoutContainer, NewTriLayoutContainerProps } from "../../../../components/player/container/NewTriLayoutContainer";
import { PlayerItem } from "../../../../components/player/interfaces";
import { PlayerActionItem, PlayerWorkBox, PlayerWorkBoxOverlayContent } from "../../../../components/workBoxes/PlayerWorkBox";
import { LayoutSelector } from "../../../../components/common/LayoutSelector";
import { LayoutType } from "../../../../interfaces/ui";
import { useInteractiveVodAspectRatios } from "../../../../hooks/videoAspectRatio";

import "./player.css";
import styles from "./player.module.css";
import clsx from "clsx";

const VOD_LAYOUTS = [LayoutType.Full, LayoutType.Top, LayoutType.Right] as const;

const isHls = (c?: ContentLocationOutputDTO) => c?.videoType === "hls";

// The content location the player actually shows for a VOD: HLS wins over file,
// otherwise the first available. Shared by `loadVods` (URL resolution) and the
// `data` memo (source build) so the two never pick different ones.
const pickContentLocation = (v: VodOutputDTO): ContentLocationOutputDTO | undefined => {
  const getPriority = (c: ContentLocationOutputDTO) => (isHls(c) ? 1 : 0);
  const contentLocations = v.contentLocations?.sort((a, b) => getPriority(b) - getPriority(a));
  return contentLocations?.length ? contentLocations[0] : undefined;
};

export interface VodPlayerContentPageProps {
  /** The meeting's playable VODs (provided by the accessibility endpoint). */
  vods: VodOutputDTO[];
  /** Header title. */
  meetingName?: string;
  /** Shown next to the title, when known. */
  expertName?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const VodPlayerContentPage = ({ vods: vodsInput, meetingName, expertName, className, style }: VodPlayerContentPageProps) => {
  const [loading, setLoading] = useState(true);
  const [vods, setVods] = useState<VodOutputDTO[]>([]);
  const [debugState, setDebugState] = useState<MultipleVideoPlayerDebugState | null>(null);
  const playerRef = useRef<MultipleVideoPlayerRef>();
  const sdkCtx = useSdkContext();
  const [settings] = useSettings();
  const gridConfig = useGridConfig();

  // Resolve each shown camera's `/cl/:id/stream` redirect to its signed S3 URL
  // ONCE, up front, and overwrite `videoUrl` in place — so the <video> src is the
  // S3 URL directly and a seek past the buffer no longer re-pays the
  // /cl -> 302 -> S3 round-trip per tile. The 7-day signed URL outlives a session.
  // On any failure (incl. a 404 from a backend without the /resolve route) we keep
  // the original /cl URL, so the tile still plays (no video bytes are downloaded).
  const resolvePlayableUrls = async (vods: VodOutputDTO[]) => {
    const client = sdkCtx.client.getHttpClient().client;
    await Promise.all(
      vods.map(async (v) => {
        const cl = pickContentLocation(v);
        const name = (v as any).device?.name;
        if (!cl || isHls(cl) || !cl.videoUrl || !name || !cl.videoUrl.includes("/stream?")) return;
        try {
          const r = await client.get(cl.videoUrl.replace("/stream?", "/resolve?"));
          if (r.status === 200 && r.data?.url) cl.videoUrl = r.data.url;
        } catch {
          // keep the /cl URL — tile still plays via the redirect path
        }
      }),
    );
  };

  // The player receives its VODs from the caller (the accessibility endpoint),
  // it never fetches them itself — that endpoint is public, so playback works
  // logged-out. We only resolve their playable URLs once (best-effort), on a
  // copy of the content locations so the signed-URL swap doesn't mutate the
  // caller's accessibility data.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Backend is filtering away non terminated VODs on the /meeting/accessibility route already
    // filtering here again just in case
    const copy = vodsInput.filter(v => v.state === VOD_STATE.TERMINATED).map((v) => ({
      ...v,
      contentLocations: v.contentLocations?.map((cl) => ({ ...cl })),
    }));
    (async () => {
      await resolvePlayableUrls(copy);
      if (!cancelled) {
        setVods(copy);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vodsInput]);

  useEffect(() => {
    if (!settings.dev) return;
    const id = setInterval(() => {
      const state = playerRef.current?.getDebugState();
      if (state) setDebugState(state);
    }, 500);
    return () => clearInterval(id);
  }, [settings.dev]);

  const data = useMemo<VideoSourceDated[]>(() => {
    const result: VideoSourceDated[] = [];
    vods.forEach((v) => {
      const contentLocation = pickContentLocation(v);
      const src = contentLocation?.videoUrl;
      const hls = isHls(contentLocation);
      const device = (v as any).device;
      const name = device?.name ?? (v as any).user?.identity?.username ?? new Date(v.recordStartedAt).toLocaleString();
      if (!contentLocation || !src) return;
      result.push({
        id: v.id,
        src: hls ? { type: "hls", src } : src,
        name,
        startedAt: new Date(v.recordStartedAt),
        endedAt: new Date(v.recordEndedAt),
      });
    });
   return result;
  }, [vods]);

  const info = useMemo<React.ReactNode>(() => {
    if (!meetingName && !expertName) return undefined;
    return (
      <div className={styles.infoContainer}>
        <div className={styles.meetingName}>{meetingName}</div>
        <div className={styles.expertName}>{expertName}</div>
      </div>
    );
  }, [meetingName, expertName]);

  const layout = useAppStoreSelector((state) => state.layout);
  const isMobile = useAppStoreSelector((state) => state.isMobile);
  const [fullscreenItemId, setFullscreenItemId] = useState<string | undefined>();

  // Interactive (playerFit) support, mirroring the live dashboard: the flag
  // and the fallback dimensions come from the VOD's backend `extra` (frozen at
  // record time) instead of the live media's `extra`. Ratios are read from the
  // <video> elements (no MediaStream here); the pan is plain page state
  // (nothing to share).
  const playerRefsMapRef = useRef(new Map<string, React.RefObject<HTMLDivElement>>());
  const getPlayerRef = useCallback((id: string) => {
    let ref = playerRefsMapRef.current.get(id);
    if (!ref) {
      ref = { current: null };
      playerRefsMapRef.current.set(id, ref);
    }
    return ref;
  }, []);
  
  const interactiveRatios = useInteractiveVodAspectRatios(
    vods,
    useCallback((id: string) => playerRefsMapRef.current.get(id)?.current ?? null, []),
  );
  const [minimapPan, setMinimapPan] = useState<Record<string, MinimapCropPan | undefined>>({});

  // Native fullscreen control inside the timeline is disabled; we drive it from
  // each player's overlay action instead.
  const controlsProps = useMemo(() => ({ header: info, displayFullscreen: false }), [info]);
  const watermarkProps = useMemo(() => ({ element: <ReactIcons.LogoFull />, bottom: 20, right: 30 }), []);

  const fullscreenContainerRef = useRef<FullscreenContainerRef>(null);

  // Sync browser fullscreen with the controlled state. Pressing Esc fires
  // onFullScreen(false), so we clear the item when that happens.
  const onFullscreenChange = useCallback((enabled: boolean) => {
    if (!enabled) setFullscreenItemId(undefined);
  }, []);

  const triLayoutRenderItem = useCallback<NonNullable<NewTriLayoutContainerProps["renderItem"]>>(
    (rendererProvider, { item, isInStrip, isFullscreen, onFocus }) => {
      const data = item.getData();
      if (data.type !== "vod_player") return null;
      const label = data.player.source.name ?? "";
      const vodId = String(data.player.source.id ?? "");

      // Interactive sources get the crop + minimap (owned by PlayerWorkBox).
      // The minimap gets a placeholder: the VOD <video> is ref-controlled by
      // MultipleVideoPlayer, so mounting it a second time would steal the ref.
      const minimapRatio = interactiveRatios[vodId];
      const isInteractive = minimapRatio !== undefined;

      const overlayContent: PlayerWorkBoxOverlayContent = isInStrip ? {
        type: "selection",
        layout,
        onSelect: onFocus,
      } : undefined;

      // Fullscreen toggle lives in the topbar.
      const topbarActions: PlayerActionItem[] = [];
      if (!isFullscreen) {
        topbarActions.push({
          key: "fullscreen",
          icon: <ReactIcons.Expand />,
          onClick: () => setFullscreenItemId(item.getId()),
          label: "Plein écran",
        });
      } else {
        topbarActions.push({
          key: "exit-fullscreen",
          icon: <ReactIcons.Shrink />,
          onClick: () => setFullscreenItemId(undefined),
          label: "Quitter le plein écran",
        });
      }
      return rendererProvider((children) => (
        <PlayerWorkBox
          label={label}
          video
          loading={data.player.stalling}
          broken={data.player.broken}
          isSmall={isInStrip}
          isSidePlayer={isInStrip}
          overlayMode="overlay-hoverable"
          topbarActions={topbarActions}
          overlayContent={overlayContent}
          topBarClassName={styles.playerTopBar}
          minimap={{
            enabled: isInteractive,
            ratio: minimapRatio,
            // Explicit null (not undefined): the provider would fall back to the
            // player node and mount the ref-controlled <video> twice. The minimap
            // stays transparent — selection frame + outside dim are enough.
            minimapContent: null,
            pan: minimapPan[vodId],
            onPanChange: (p) => setMinimapPan((prev) => ({ ...prev, [vodId]: p })),
            // Top-anchored so the stack doesn't sit on the VOD timeline bar.
            position: "top",
          }}
          containerRef={getPlayerRef(vodId)}
        >
          {children}
        </PlayerWorkBox>
      ));
    },
    [layout, interactiveRatios, minimapPan, getPlayerRef],
  );

  const renderPlayersLayout = useCallback(
    (players: PlayerRenderItem[], ctx: MultipleVideoPlayerRenderingContext) => {
      const items = players.map((player) => new PlayerItem({ type: "vod_player", player, hidden: !player.display }));
      return (
        <div className={clsx(isMobile ? styles.playersLayoutMobile : styles.playersLayout, { [styles.hideControls]: !ctx.displayControls })}>
          {!fullscreenItemId && (
            <LayoutSelector
              className={styles.layoutSelector}
              orientation={isMobile ? "horizontal" : "vertical"}
              layouts={VOD_LAYOUTS}
              defaultLayout={LayoutType.Top}
            />
          )}
          <div className={styles.playersLayoutContent}>
            <NewTriLayoutContainer
              items={items}
              layout={layout}
              gridMode={gridConfig.gridMode}
              pageSize={gridConfig.pageSize}
              autoTileMinPx={gridConfig.autoTileMinPx}
              fullscreenItemId={fullscreenItemId}
              renderItem={triLayoutRenderItem}
              style={{ padding: fullscreenItemId ? 0 : 16, boxSizing: "border-box" }}
            />
          </div>
        </div>
      );
    },
    [isMobile, layout, fullscreenItemId, triLayoutRenderItem, gridConfig],
  );

  if (loading) return <Loading fullscreen message="Chargement en cours..." style={style} />;

  const renderContent = () => {
    return (
      <FullscreenContainer
        ref={fullscreenContainerRef}
        useNative
        fullscreen={!!fullscreenItemId}
        onFullScreen={onFullscreenChange}
        style={{ width: "100%", height: "100%" }}
      >
        <>
          <MultipleVideoPlayer
            ref={playerRef}
            className={styles.vods}
            controls={controlsProps}
            //watermark={watermarkProps}
            dateDisplayMode={MultipleVideoPlayerDateDisplayMode.NONE}
            data={data}
            renderPlayersLayout={renderPlayersLayout}
          />
          <MultiVideoPlayerDebugPane state={debugState} className={styles.debugOverlay} />
        </>
      </FullscreenContainer>
    );
  };
  const classes = [styles.container];
  if (className) classes.push(className);
  return (
    <div className={classes.join(" ")} style={style}>
      {renderContent()}
    </div>
  );
};

